<template>
  <div class="page">
    <div class="seiten-kopf">
      <router-link to="/einkaufsliste" class="seiten-kopf-zurueck" aria-label="Zurück">
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
      </router-link>
      <h1>Gekaufte Zutaten</h1>
    </div>

    <div v-if="Einkaufslisten.length > 1" class="listen-wahl">
      <font-awesome-icon icon="fa-solid fa-list" class="listen-wahl-icon" />
      <select :value="AktiveEinkaufsliste.Id" @change="ListeWechseln($event)" class="listen-wahl-select" aria-label="Einkaufsliste wählen">
        <option v-for="Liste in Einkaufslisten" :key="Liste.Id" :value="Liste.Id">{{ Liste.Name }}</option>
      </select>
    </div>

    <p v-if="Fehlermeldung" class="error-message">{{ Fehlermeldung }}</p>

    <button v-if="Gekaufte.length > 0" type="button" class="btn btn-secondary leeren-button" @click="Leeren">Liste leeren</button>

    <p v-if="Gekaufte.length === 0" class="hint-message">Noch keine gekauften Zutaten.</p>

    <template v-else>
      <div class="liste-suche">
        <input v-model="Suche" type="text" placeholder="Suchen..." class="liste-suche-feld" />
        <button type="button" class="btn btn-secondary liste-falten-btn" @click="FoldUnfoldAlle">
          {{ AlleEingeklappt ? 'Ausklappen' : 'Einklappen' }}
        </button>
      </div>
      <p v-if="Gruppen.length === 0" class="hint-message">Keine Einträge gefunden.</p>
      <div v-else class="eintrag-liste-scroll">
        <div class="kategorie-gruppe" v-for="Gruppe in Gruppen" :key="Gruppe.DatumSchluessel">
          <h2 class="kategorie-titel" @click="DatumToggeln(Gruppe.DatumSchluessel)">
            <span>{{ Gruppe.Label }}</span>
            <span class="kategorie-badge">{{ Gruppe.Eintraege.length }}</span>
            <font-awesome-icon icon="fa-solid fa-chevron-right" class="kategorie-pfeil" :class="{ 'kategorie-pfeil--offen': !GeklapptTage.includes(Gruppe.DatumSchluessel) }" />
          </h2>
          <div v-show="!GeklapptTage.includes(Gruppe.DatumSchluessel)" class="eintrag-liste">
            <div class="eintrag-zeile" v-for="Eintrag in Gruppe.Eintraege" :key="Eintrag.Id">
              <div class="eintrag-mit-bild">
                <img
                  v-if="Eintrag.HatBild"
                  :src="ZutatBildUrl(Eintrag)"
                  class="mini-bild"
                  alt=""
                  loading="lazy"
                  @error="$event.target.style.display = 'none'"
                />
                <div class="eintrag-info">
                  <span class="eintrag-name">{{ Eintrag.Name }}</span>
                  <span class="eintrag-details">{{ MengeText(Eintrag) }}</span>
                  <span v-if="Eintrag.Quellen && Eintrag.Quellen.length > 0" class="eintrag-quelle">
                    Gehört zu: {{ Eintrag.Quellen.map(Q => Q.RezeptName).join(', ') }}
                  </span>
                </div>
              </div>
              <div class="eintrag-aktionen">
                <button type="button" class="aktion-button hinzufuegen" @click="ZurueckAufEinkaufsliste(Eintrag.Id)">Zurück auf Einkaufsliste</button>
                <button type="button" class="aktion-button loeschen" @click="Entfernen(Eintrag)">Aus Liste entfernen</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<script>
import {
  Einkaufslistenpositionen,
  EinkaufslisteLaden,
  Einkaufslisten,
  EinkaufslistenLaden,
  AktiveEinkaufsliste,
  AktiveEinkaufslisteSetzen,
  EinkaufslisteFinden,
  Zutaten,
  ZutatenLaden,
  ZutatFinden,
  GekaufteZutatEntfernen,
  GekaufteZutatZurueckAufEinkaufsliste,
  GekaufteZutatenLeeren
} from '../composables/useZutaten'
import { BildUrl } from '../composables/useBilder'

const Wochentage = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
const Monate = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']

function DatumSchluessel(DatumStr) {
  const D = new Date(DatumStr)
  return `${D.getFullYear()}-${String(D.getMonth() + 1).padStart(2, '0')}-${String(D.getDate()).padStart(2, '0')}`
}

function GleicherTag(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function DatumLabel(DatumStr) {
  const D = new Date(DatumStr)
  const Heute = new Date()
  const Gestern = new Date(Heute)
  Gestern.setDate(Heute.getDate() - 1)
  if (GleicherTag(D, Heute)) return 'Heute'
  if (GleicherTag(D, Gestern)) return 'Gestern'
  return `${Wochentage[D.getDay()]}, ${D.getDate()}. ${Monate[D.getMonth()]}`
}

export default {
  name: 'GekaufteZutatenAnzeigenView',
  data() {
    return {
      Einkaufslistenpositionen,
      Einkaufslisten,
      AktiveEinkaufsliste,
      Zutaten,
      Fehlermeldung: '',
      Suche: '',
      GeklapptTage: []
    }
  },
  async created() {
    await Promise.all([ZutatenLaden(), EinkaufslisteLaden(), EinkaufslistenLaden()])
  },
  computed: {
    Gekaufte() {
      // Name/KategorieId immer live von der Zutat ableiten, siehe EinkaufslisteAnzeigenView.vue
      return this.Einkaufslistenpositionen
        .filter(Eintrag => Eintrag.Gekauft && Eintrag.EinkaufslisteId === this.AktiveEinkaufsliste.Id)
        .map(Eintrag => {
          const Zutat = ZutatFinden(Eintrag.ZutatId)
          return {
            ...Eintrag,
            Name: Zutat ? Zutat.Name : Eintrag.Name,
            KategorieId: Zutat ? Zutat.KategorieId : Eintrag.KategorieId,
            HatBild: Zutat ? Zutat.HatBild : 0,
            BildVersion: Zutat ? Zutat.BildVersion : null
          }
        })
    },
    GefilterteGekaufte() {
      const s = this.Suche.toLowerCase().trim()
      if (!s) return this.Gekaufte
      return this.Gekaufte.filter(e => e.Name.toLowerCase().includes(s))
    },
    AlleEingeklappt() {
      return this.Gruppen.length > 0 && this.GeklapptTage.length >= this.Gruppen.length
    },
    Gruppen() {
      // Nach Kaufdatum gruppiert (neueste zuerst), statt nach Kategorie - GekauftAm
      // fehlt nur bei Randfaellen vor der Migration, dann faellt es auf ErstelltAm zurueck.
      const Basis = this.GefilterteGekaufte
      const NachDatum = new Map()
      for (const Eintrag of Basis) {
        const Stempel = Eintrag.GekauftAm || Eintrag.ErstelltAm
        const Schluessel = DatumSchluessel(Stempel)
        if (!NachDatum.has(Schluessel)) {
          NachDatum.set(Schluessel, { DatumSchluessel: Schluessel, Label: DatumLabel(Stempel), Eintraege: [] })
        }
        NachDatum.get(Schluessel).Eintraege.push(Eintrag)
      }
      for (const Gruppe of NachDatum.values()) {
        Gruppe.Eintraege.sort((a, b) => new Date(b.GekauftAm || b.ErstelltAm) - new Date(a.GekauftAm || a.ErstelltAm))
      }
      return Array.from(NachDatum.values()).sort((a, b) => b.DatumSchluessel.localeCompare(a.DatumSchluessel))
    }
  },
  methods: {
    ListeWechseln(Ereignis) {
      AktiveEinkaufslisteSetzen(Number(Ereignis.target.value))
    },
    DatumToggeln(Schluessel) {
      const Index = this.GeklapptTage.indexOf(Schluessel)
      if (Index === -1) {
        this.GeklapptTage.push(Schluessel)
      } else {
        this.GeklapptTage.splice(Index, 1)
      }
    },
    FoldUnfoldAlle() {
      if (this.AlleEingeklappt) {
        this.GeklapptTage = []
      } else {
        this.GeklapptTage = this.Gruppen.map(g => g.DatumSchluessel)
      }
    },
    ZutatBildUrl(Eintrag) {
      return BildUrl('Zutat', Eintrag.ZutatId, Eintrag.BildVersion)
    },
    MengeText(Eintrag) {
      const Menge = Eintrag.Menge != null ? +Eintrag.Menge : ''
      const Basis = `${Menge} ${Eintrag.Einheit || ''}`.trim()
      const Anzahl = Eintrag.Anzahl || 1
      if (!Basis) return Anzahl > 1 ? `${Anzahl}×` : ''
      return Anzahl > 1 ? `${Anzahl} × ${Basis}` : Basis
    },
    async ZurueckAufEinkaufsliste(Id) {
      this.Fehlermeldung = ''
      try {
        await GekaufteZutatZurueckAufEinkaufsliste(Id)
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
      }
    },
    async Entfernen(Eintrag) {
      this.Fehlermeldung = ''
      if (confirm(`"${Eintrag.Name}" wirklich endgültig entfernen?`)) {
        try {
          await GekaufteZutatEntfernen(Eintrag.Id)
        } catch (Fehler) {
          this.Fehlermeldung = Fehler.message
        }
      }
    },
    async Leeren() {
      this.Fehlermeldung = ''
      const ListenName = EinkaufslisteFinden(this.AktiveEinkaufsliste.Id)?.Name || 'Einkaufsliste'
      if (confirm(`Gekaufte Zutaten von „${ListenName}" wirklich vollständig leeren?`)) {
        try {
          await GekaufteZutatenLeeren(this.AktiveEinkaufsliste.Id)
        } catch (Fehler) {
          this.Fehlermeldung = Fehler.message
        }
      }
    }
  }
}
</script>

<style scoped>
.listen-wahl {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 12px;
  max-width: 320px;
  padding: 6px 12px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-alt);
}

.listen-wahl-icon {
  color: var(--color-text-muted);
  font-size: 0.85rem;
  flex-shrink: 0;
}

.listen-wahl-select {
  flex: 1;
  border: none;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 0.95rem;
  font-weight: 600;
  outline: none;
  min-width: 0;
}
</style>
